import React, { useState } from "react";

import Select from "../../../../components/input/select/Select";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import CheckBox from "../../../../components/input/checkbox/CheckBox";

import {
  FormSection,
  FormRow,
} from "../../../../components/layout/forms/Index";

import TalentModal from "./TalentModal";
import { clean, getApiError } from "../utils/talentUtils";

import "./TalentForms.css";

const asDate = (value) => (value ? String(value).slice(0, 10) : "");

const initialFields = (initial) => ({
  talentSportId: initial?.talentSportId ?? "",
  title: initial?.title || "",
  organisation: initial?.organisation || "",
  experienceType: initial?.experienceType || "",
  position: initial?.position || "",
  startDate: asDate(initial?.startDate),
  endDate: asDate(initial?.endDate),
  isCurrent: Boolean(initial?.isCurrent),
  description: initial?.description || "",
});

export default function TalentExperienceForm({
  initial,
  sports = [],
  saving = false,
  onSave,
  onClose,
}) {
  const [fields, setFields] = useState(() => initialFields(initial));
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const change = ({ target }) => {
    setFields((previous) => ({
      ...previous,
      [target.name]: target.value,
    }));

    setErrors((previous) => ({
      ...previous,
      [target.name]: "",
    }));
  };

  const handleCurrentChange = (checked) => {
    setFields((previous) => ({
      ...previous,
      isCurrent: checked,
      endDate: checked ? "" : previous.endDate,
    }));

    setErrors((previous) => ({
      ...previous,
      endDate: "",
    }));
  };

  const sportOptions = [
    { label: "General / Not specified", value: "" },
    ...sports.map((sport) => ({
      label: sport.sport,
      value: String(sport.sportId),
    })),
  ];

  const save = async () => {
    if (saving) return;

    const next = {};

    if (!clean(fields.title)) {
      next.title = "Title is required.";
    }

    if (
      !fields.isCurrent &&
      fields.startDate &&
      fields.endDate &&
      fields.startDate > fields.endDate
    ) {
      next.endDate = "End date must follow start date.";
    }

    if (
      fields.talentSportId !== "" &&
      !sports.some(
        (sport) => String(sport.sportId) === String(fields.talentSportId),
      )
    ) {
      next.talentSportId = "Select a valid sport.";
    }

    setErrors(next);

    if (Object.keys(next).length) return;

    const payload = {
      talentSportId:
        fields.talentSportId === "" ? null : Number(fields.talentSportId),
      title: clean(fields.title),
      organisation: clean(fields.organisation) || null,
      experienceType: clean(fields.experienceType) || null,
      position: clean(fields.position) || null,
      startDate: fields.startDate || null,
      endDate: fields.isCurrent ? null : fields.endDate || null,
      isCurrent: fields.isCurrent,
      description: clean(fields.description) || null,
    };

    try {
      setError("");
      await onSave(payload);
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <TalentModal
      title={initial ? "Edit Experience" : "Add Experience"}
      description={
        initial
          ? "Update your sporting experience, team or competition details."
          : "Record your experience with teams, clubs, schools and competitions."
      }
      busy={saving}
      onClose={onClose}
      onSave={save}
    >
      {error && (
        <p
          className="user-talent__error"
          role="alert"
          style={{ marginBottom: "1rem" }}
        >
          {error}
        </p>
      )}

      <FormSection>
        <Select
          label="Sport"
          name="talentSportId"
          value={String(fields.talentSportId)}
          onChange={change}
          items={sportOptions}
          error={errors.talentSportId}
        />

        <TextField
          label="Experience Title"
          name="title"
          value={fields.title}
          onChange={change}
          placeholder="e.g. First XV"
          maxLength={150}
          required
        />

        <TextField
          label="Organisation"
          name="organisation"
          value={fields.organisation}
          onChange={change}
          placeholder="School, club or team"
          maxLength={150}
        />

        <FormRow>
          <TextField
            label="Experience Type"
            name="experienceType"
            value={fields.experienceType}
            onChange={change}
            placeholder="e.g. School, Club"
            maxLength={60}
          />

          <TextField
            label="Position"
            name="position"
            value={fields.position}
            onChange={change}
            maxLength={100}
          />
        </FormRow>

        <FormRow>
          <TextField
            label="Start Date"
            name="startDate"
            type="date"
            value={fields.startDate}
            onChange={change}
          />

          {!fields.isCurrent && (
            <TextField
              label="End Date"
              name="endDate"
              type="date"
              value={fields.endDate}
              onChange={change}
            />
          )}
        </FormRow>

        <div className="talent-form__checkbox">
          <CheckBox
            name="isCurrent"
            label="Current experience"
            checked={fields.isCurrent}
            onChange={handleCurrentChange}
          />
        </div>

        <TextArea
          label="Description"
          name="description"
          value={fields.description}
          onChange={change}
          placeholder="Describe your experience..."
          maxLength={10000}
          maxHeight={140}
        />
      </FormSection>
    </TalentModal>
  );
}
